import React, { useEffect, useState } from "react";
import PublicLayout from "../components/public/PublicLayout";
import { navigate } from "../utils/router";
import {
  getPublicLeague,
  getPublicLeagueTeams,
  getPublicLeagueFixtures,
  getPublicLeagueStandings,
} from "../store/publicLeagues";
import type { League, LeagueTeam, LeagueFixture, LeagueStanding } from "../store/leagues";

type Tab = "overview" | "fixtures" | "results" | "teams" | "table";

interface Props {
  leagueId: string;
}

export default function LeagueDetailPage({ leagueId }: Props) {
  const [league, setLeague] = useState<League | null>(null);
  const [teams, setTeams] = useState<LeagueTeam[]>([]);
  const [fixtures, setFixtures] = useState<LeagueFixture[]>([]);
  const [standings, setStandings] = useState<LeagueStanding[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>("overview");

  useEffect(() => {
    let mounted = true;
    async function load() {
      const l = await getPublicLeague(leagueId);
      if (!mounted) return;
      if (!l) {
        setLoading(false);
        return;
      }
      setLeague(l);

      const [t, f, s] = await Promise.all([
        getPublicLeagueTeams(leagueId),
        getPublicLeagueFixtures(leagueId),
        getPublicLeagueStandings(leagueId),
      ]);
      if (!mounted) return;
      setTeams(t);
      setFixtures(f);
      setStandings(s);
      setLoading(false);
    }
    load();
    return () => { mounted = false; };
  }, [leagueId]);

  if (loading) {
    return (
      <PublicLayout activePath="/leagues" showBottomNav>
        <div className="max-w-6xl mx-auto px-4 pt-8 pb-8">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 bg-white/5 rounded" />
            <div className="h-4 w-32 bg-white/5 rounded" />
            <div className="h-12 w-full bg-white/5 rounded-xl mt-6" />
            <div className="h-40 w-full bg-white/5 rounded-2xl mt-4" />
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (!league) {
    return (
      <PublicLayout activePath="/leagues" showBottomNav>
        <div className="max-w-6xl mx-auto px-4 pt-8 pb-8 text-center">
          <div className="ld-card rounded-2xl p-10">
            <h2 className="text-xl font-bold text-white mb-2">League Not Found</h2>
            <p className="text-slate-400/70 text-sm mb-4">This league may be private or does not exist.</p>
            <button
              onClick={() => navigate("/leagues")}
              className="text-emerald-400 hover:text-emerald-300 font-semibold text-sm"
            >
              Back to Leagues
            </button>
          </div>
        </div>
      </PublicLayout>
    );
  }

  const upcomingFixtures = fixtures.filter((f) => f.status === "upcoming" || f.status === "scheduled");
  const completedFixtures = fixtures.filter((f) => f.status === "completed" || f.status === "played");

  const tabs: { key: Tab; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "fixtures", label: "Fixtures" },
    { key: "results", label: "Results" },
    { key: "teams", label: "Teams" },
    { key: "table", label: "Table" },
  ];

  return (
    <PublicLayout activePath="/leagues" showBottomNav>
      <div className="max-w-6xl mx-auto px-4 pt-5 pb-8">
        {/* Back button */}
        <button
          onClick={() => navigate("/leagues")}
          className="flex items-center gap-1.5 text-slate-400 hover:text-white text-sm mb-4 transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          All Leagues
        </button>

        {/* League Header */}
        <div className="ld-card rounded-2xl p-5 mb-4">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-black text-white">{league.name}</h1>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <span className="bg-emerald-500/15 text-emerald-400 text-xs font-bold px-2.5 py-1 rounded-lg border border-emerald-500/20">
                  {league.format}
                </span>
                <span className="text-slate-400 text-sm">{league.overs} overs</span>
                {league.competition_type && (
                  <span className="text-slate-400/70 text-xs">{league.competition_type}</span>
                )}
              </div>
            </div>
          </div>
          {(league.location || league.start_date) && (
            <div className="flex items-center gap-4 mt-3 flex-wrap text-sm text-slate-400/70">
              {league.location && (
                <span className="flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  </svg>
                  {league.location}
                </span>
              )}
              {league.start_date && (
                <span className="flex items-center gap-1">
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {new Date(league.start_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  {league.end_date && ` - ${new Date(league.end_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`}
                </span>
              )}
            </div>
          )}
          {/* Quick stats */}
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div className="bg-white/[0.03] rounded-lg p-3 text-center">
              <div className="text-lg font-black text-white">{teams.length}</div>
              <div className="text-[11px] text-slate-400/70 font-medium">Teams</div>
            </div>
            <div className="bg-white/[0.03] rounded-lg p-3 text-center">
              <div className="text-lg font-black text-white">{fixtures.length}</div>
              <div className="text-[11px] text-slate-400/70 font-medium">Fixtures</div>
            </div>
            <div className="bg-white/[0.03] rounded-lg p-3 text-center">
              <div className="text-lg font-black text-white">{completedFixtures.length}</div>
              <div className="text-[11px] text-slate-400/70 font-medium">Played</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto pb-1 mb-4 scrollbar-hide">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${
                activeTab === tab.key
                  ? "bg-emerald-600/20 text-emerald-400 border border-emerald-500/30"
                  : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="ld-card rounded-2xl p-5">
          {activeTab === "overview" && (
            <OverviewTab
              league={league}
              teams={teams}
              upcomingFixtures={upcomingFixtures}
              standings={standings}
            />
          )}
          {activeTab === "fixtures" && (
            <FixturesTab fixtures={upcomingFixtures} league={league} />
          )}
          {activeTab === "results" && (
            <ResultsTab fixtures={completedFixtures} league={league} />
          )}
          {activeTab === "teams" && (
            <TeamsTab teams={teams} />
          )}
          {activeTab === "table" && (
            <TableTab standings={standings} />
          )}
        </div>
      </div>

      <style>{`
        .ld-card {
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </PublicLayout>
  );
}

function OverviewTab({
  league,
  teams,
  upcomingFixtures,
  standings,
}: {
  league: League;
  teams: LeagueTeam[];
  upcomingFixtures: LeagueFixture[];
  standings: LeagueStanding[];
}) {
  const nextFixture = upcomingFixtures[0];
  const topTeams = standings.slice(0, 3);

  return (
    <div className="space-y-5 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-5 lg:items-start">
      {/* Next Fixture */}
      {nextFixture && (
        <div>
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Next Fixture</h3>
          <div className="bg-white/[0.03] rounded-xl p-4 border border-white/5">
            <div className="flex items-center justify-between">
              <span className="text-white font-bold text-sm">
                {nextFixture.team_a_name || nextFixture.home_team_name || "TBD"}
              </span>
              <span className="text-slate-400 text-xs px-2">vs</span>
              <span className="text-white font-bold text-sm">
                {nextFixture.team_b_name || nextFixture.away_team_name || "TBD"}
              </span>
            </div>
            <div className="flex items-center gap-3 mt-2 text-xs text-slate-400/70">
              {nextFixture.scheduled_at && (
                <span>{new Date(nextFixture.scheduled_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
              )}
              {nextFixture.venue && <span>{nextFixture.venue}</span>}
              {nextFixture.round_number && <span>Round {nextFixture.round_number}</span>}
            </div>
          </div>
        </div>
      )}

      {/* Mini Standings */}
      {topTeams.length > 0 && (
        <div>
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Top of the Table</h3>
          <div className="space-y-1.5">
            {topTeams.map((s, i) => (
              <div key={s.id} className="flex items-center justify-between bg-white/[0.03] rounded-lg p-3">
                <div className="flex items-center gap-2.5">
                  <span className={`text-xs font-black w-5 text-center ${
                    i === 0 ? "text-emerald-400" : "text-slate-400"
                  }`}>{i + 1}</span>
                  <span className="text-white text-sm font-semibold">{s.team_name}</span>
                </div>
                <span className="text-emerald-400 text-sm font-bold">{s.points} pts</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Teams list */}
      {teams.length > 0 && (
        <div>
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Teams ({teams.length})</h3>
          <div className="flex flex-wrap gap-2">
            {teams.map((t) => (
              <span key={t.id} className="bg-white/[0.04] border border-white/8 text-white text-xs font-medium px-3 py-1.5 rounded-lg">
                {t.team_name}
              </span>
            ))}
          </div>
        </div>
      )}

      {!nextFixture && topTeams.length === 0 && teams.length === 0 && (
        <div className="text-center py-6">
          <p className="text-slate-400/70 text-sm">This league has no data yet.</p>
        </div>
      )}
    </div>
  );
}

function FixturesTab({ fixtures, league }: { fixtures: LeagueFixture[]; league: League }) {
  if (fixtures.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-slate-400/70 text-sm">No upcoming fixtures scheduled</p>
      </div>
    );
  }

  const grouped = groupByRound(fixtures);

  return (
    <div className="space-y-5">
      {Object.entries(grouped).map(([round, roundFixtures]) => (
        <div key={round}>
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
            Round {round}
          </h3>
          <div className="space-y-2">
            {roundFixtures.map((f) => (
              <FixtureRow key={f.id} fixture={f} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ResultsTab({ fixtures, league }: { fixtures: LeagueFixture[]; league: League }) {
  if (fixtures.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-slate-400/70 text-sm">No results yet</p>
      </div>
    );
  }

  const grouped = groupByRound(fixtures);

  return (
    <div className="space-y-5">
      {Object.entries(grouped).map(([round, roundFixtures]) => (
        <div key={round}>
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
            Round {round}
          </h3>
          <div className="space-y-2">
            {roundFixtures.map((f) => (
              <div key={f.id} className="bg-white/[0.03] rounded-xl p-3 border border-white/5">
                <div className="flex items-center justify-between">
                  <span className="text-white font-semibold text-sm">
                    {f.team_a_name || f.home_team_name || "TBD"}
                  </span>
                  <span className="text-slate-400 text-xs">vs</span>
                  <span className="text-white font-semibold text-sm">
                    {f.team_b_name || f.away_team_name || "TBD"}
                  </span>
                </div>
                {f.result_text && (
                  <div className="text-emerald-400 text-xs font-medium mt-1.5 truncate">{f.result_text}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function TeamsTab({ teams }: { teams: LeagueTeam[] }) {
  if (teams.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-slate-400/70 text-sm">No teams registered yet</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {teams.map((team) => (
        <div key={team.id} className="bg-white/[0.03] rounded-xl p-4 border border-white/5 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-800 to-emerald-900 border border-emerald-600/30 flex items-center justify-center flex-shrink-0">
            {team.team_logo_url ? (
              <img src={team.team_logo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <span className="text-emerald-300 text-sm font-bold">{team.team_name.charAt(0)}</span>
            )}
          </div>
          <span className="text-white font-semibold text-sm">{team.team_name}</span>
        </div>
      ))}
    </div>
  );
}

function TableTab({ standings }: { standings: LeagueStanding[] }) {
  if (standings.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-slate-400/70 text-sm">Standings not yet available</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/10">
            <th className="text-left p-2 text-slate-400 text-xs font-semibold">#</th>
            <th className="text-left p-2 text-slate-400 text-xs font-semibold">Team</th>
            <th className="text-center p-2 text-slate-400 text-xs font-semibold">P</th>
            <th className="text-center p-2 text-slate-400 text-xs font-semibold hidden sm:table-cell">W</th>
            <th className="text-center p-2 text-slate-400 text-xs font-semibold hidden sm:table-cell">L</th>
            <th className="text-center p-2 text-slate-400 text-xs font-semibold">Pts</th>
            <th className="text-right p-2 text-slate-400 text-xs font-semibold">NRR</th>
          </tr>
        </thead>
        <tbody>
          {standings.map((s, i) => (
            <tr key={s.id} className="border-b border-white/5 last:border-0">
              <td className={`p-2 font-bold text-xs ${i === 0 ? "text-emerald-400" : "text-slate-400"}`}>
                {i + 1}
              </td>
              <td className="p-2 text-white font-semibold text-sm">{s.team_name}</td>
              <td className="p-2 text-center text-slate-300">{s.matches_played}</td>
              <td className="p-2 text-center text-slate-300 hidden sm:table-cell">{s.wins}</td>
              <td className="p-2 text-center text-slate-300 hidden sm:table-cell">{s.losses}</td>
              <td className="p-2 text-center text-emerald-400 font-bold">{s.points}</td>
              <td className="p-2 text-right text-slate-400 text-xs">
                {s.net_run_rate > 0 ? "+" : ""}{s.net_run_rate.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FixtureRow({ fixture }: { fixture: LeagueFixture }) {
  return (
    <div className="bg-white/[0.03] rounded-xl p-3 border border-white/5">
      <div className="flex items-center justify-between">
        <span className="text-white font-semibold text-sm">
          {fixture.team_a_name || fixture.home_team_name || "TBD"}
        </span>
        <span className="text-slate-400 text-xs px-2">vs</span>
        <span className="text-white font-semibold text-sm">
          {fixture.team_b_name || fixture.away_team_name || "TBD"}
        </span>
      </div>
      <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-400/70">
        {fixture.scheduled_at && (
          <span>{new Date(fixture.scheduled_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
        )}
        {fixture.venue && <span>{fixture.venue}</span>}
      </div>
    </div>
  );
}

function groupByRound(fixtures: LeagueFixture[]): Record<string, LeagueFixture[]> {
  const groups: Record<string, LeagueFixture[]> = {};
  for (const f of fixtures) {
    const key = String(f.round_number || f.round || "1");
    if (!groups[key]) groups[key] = [];
    groups[key].push(f);
  }
  return groups;
}
